import React, { useState, useEffect, useCallback } from 'react';
import './DeadlineCountdown.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const DEFAULT_DEADLINE = '2026-02-28T23:59:00+08:00';

function DeadlineCountdown({
  language,
  onDeadlineStatusChange,
  previewMode = false,
  targetDate = DEFAULT_DEADLINE
}) {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [loading, setLoading] = useState(true);

  const deadlineTimestamp = new Date(targetDate).getTime();
  const isPreviewTarget = previewMode && targetDate !== DEFAULT_DEADLINE;

  const texts = {
    zh: {
      title: '投稿倒计时',
      deadline: '截止时间',
      deadlineDate: isPreviewTarget ? '2027年4月10日 23:59' : '2026年2月28日 23:59',
      days: '天',
      hours: '时',
      minutes: '分',
      seconds: '秒',
      closed: '投稿已关闭',
      closedDesc: '感谢您的关注，投稿通道已关闭',
      cfpOnly: '您仍可查看征稿文件'
    },
    en: {
      title: 'SUBMISSION COUNTDOWN',
      deadline: 'DEADLINE',
      deadlineDate: isPreviewTarget ? 'Apr 10, 2027 23:59 (Beijing)' : 'Feb 28, 2026 23:59 (Beijing)',
      days: 'DAYS',
      hours: 'HRS',
      minutes: 'MIN',
      seconds: 'SEC',
      closed: 'SUBMISSIONS CLOSED',
      closedDesc: 'Thank you for your interest. Submissions are now closed.',
      cfpOnly: 'You may still view the Call for Papers'
    }
  };

  const currentTexts = texts[language] || texts.en;

  const setPreviewState = useCallback(() => {
    const remaining = Math.max(0, deadlineTimestamp - Date.now());
    const nextIsOpen = remaining > 0;
    setTimeRemaining(remaining);
    setIsOpen(nextIsOpen);

    if (onDeadlineStatusChange) {
      onDeadlineStatusChange(nextIsOpen);
    }

    setLoading(false);
  }, [deadlineTimestamp, onDeadlineStatusChange]);

  const fetchDeadlineStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/deadline`);
      const data = await response.json();

      setIsOpen(data.isOpen);
      setTimeRemaining(data.remainingMs);

      if (onDeadlineStatusChange) {
        onDeadlineStatusChange(data.isOpen);
      }
    } catch (error) {
      console.error('Failed to fetch deadline status:', error);
      const fallbackDeadline = new Date(DEFAULT_DEADLINE).getTime();
      const remaining = Math.max(0, fallbackDeadline - Date.now());
      const nextIsOpen = remaining > 0;
      setTimeRemaining(remaining);
      setIsOpen(nextIsOpen);

      if (onDeadlineStatusChange) {
        onDeadlineStatusChange(nextIsOpen);
      }
    } finally {
      setLoading(false);
    }
  }, [onDeadlineStatusChange]);

  useEffect(() => {
    if (previewMode) {
      setPreviewState();
      return undefined;
    }

    fetchDeadlineStatus();

    const syncInterval = setInterval(fetchDeadlineStatus, 5 * 60 * 1000);

    return () => clearInterval(syncInterval);
  }, [fetchDeadlineStatus, previewMode, setPreviewState]);

  useEffect(() => {
    if (loading || !isOpen) return undefined;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev === null) return null;
        const newValue = Math.max(0, prev - 1000);

        if (newValue === 0) {
          setIsOpen(false);
          if (onDeadlineStatusChange) {
            onDeadlineStatusChange(false);
          }
        }

        return newValue;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [loading, isOpen, onDeadlineStatusChange]);

  const formatTime = (ms) => {
    if (ms === null || ms <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = totalSeconds % 60;

    return { days, hours, minutes, seconds };
  };

  const padZero = (num) => String(num).padStart(2, '0');
  const time = formatTime(timeRemaining);

  if (loading) {
    return (
      <div className="countdown-container">
        <div className="countdown-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!isOpen) {
    return (
      <div className="countdown-container closed">
        <div className="countdown-frame">
          <div className="countdown-corner tl"></div>
          <div className="countdown-corner tr"></div>
          <div className="countdown-corner bl"></div>
          <div className="countdown-corner br"></div>

          <div className="countdown-closed">
            <div className="closed-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h2 className="closed-title">{currentTexts.closed}</h2>
            <p className="closed-desc">{currentTexts.closedDesc}</p>
            <p className="closed-cfp">{currentTexts.cfpOnly}</p>
          </div>

          <div className="countdown-scanline"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="countdown-container">
      <div className="countdown-frame">
        <div className="countdown-corner tl"></div>
        <div className="countdown-corner tr"></div>
        <div className="countdown-corner bl"></div>
        <div className="countdown-corner br"></div>

        <div className="countdown-header">
          <div className="countdown-status-light"></div>
          <span className="countdown-title">{currentTexts.title}</span>
        </div>

        <div className="countdown-deadline-info">
          <span className="deadline-label">{currentTexts.deadline}</span>
          <span className="deadline-date">{currentTexts.deadlineDate}</span>
        </div>

        <div className="countdown-display">
          <div className="countdown-unit">
            <div className="countdown-value-wrapper">
              <span className="countdown-value">{time.days}</span>
              <div className="value-glow"></div>
            </div>
            <span className="countdown-label">{currentTexts.days}</span>
          </div>

          <div className="countdown-separator">:</div>

          <div className="countdown-unit">
            <div className="countdown-value-wrapper">
              <span className="countdown-value">{padZero(time.hours)}</span>
              <div className="value-glow"></div>
            </div>
            <span className="countdown-label">{currentTexts.hours}</span>
          </div>

          <div className="countdown-separator">:</div>

          <div className="countdown-unit">
            <div className="countdown-value-wrapper">
              <span className="countdown-value">{padZero(time.minutes)}</span>
              <div className="value-glow"></div>
            </div>
            <span className="countdown-label">{currentTexts.minutes}</span>
          </div>

          <div className="countdown-separator blink">:</div>

          <div className="countdown-unit seconds">
            <div className="countdown-value-wrapper">
              <span className="countdown-value">{padZero(time.seconds)}</span>
              <div className="value-glow pulse"></div>
            </div>
            <span className="countdown-label">{currentTexts.seconds}</span>
          </div>
        </div>

        <div className="countdown-scanline"></div>
      </div>
    </div>
  );
}

export default DeadlineCountdown;
